import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { AuthProvider } from './contexts/AuthContext';
import { FeaturesProvider, useFeatures } from './contexts/FeaturesContext';
import { ViewProvider, useView } from './contexts/ViewContext';
import { WorkspaceProvider } from './contexts/WorkspaceContext';
import { TimerProvider } from './contexts/TimerContext';
import NotificationModal from './components/NotificationModal';
import { ToastProvider } from './contexts/ToastContext';
import ToastContainer from './components/shared/ToastContainer';
import { verifyToken } from './api';
import { supabase } from './lib/supabase';

import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import UserManagementPage from './pages/UserManagementPage';
import CompanyManagementPage from './pages/CompanyManagementPage';
import ProjectManagementPage from './pages/ProjectManagementPage';
import ProfilePage from './pages/ProfilePage';
import UpsellNotificationsPage from './pages/UpsellNotificationsPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import TranscriptDetailPage from './pages/TranscriptDetailPage';

import ClientDashboard from './pages/ClientDashboard';
import ClientProjectDetailPage from './pages/ClientProjectDetailPage';
import ClientTranscriptDetailPage from './pages/ClientTranscriptDetailPage';
import SubscribeUpsellPage from './pages/SubscribeUpsellPage';
import CountSummaryPage from "./pages/CountSummaryPage";
import PriceComparisonPage from './pages/PriceComparisonPage';

import GoogleAdsPage from './pages/GoogleAdsPage';
import GoogleAnalyticsPage from './pages/GoogleAnalyticsPage';
import BookingsPage from './pages/BookingsPage';
import CdpDashboardPage from './pages/CdpDashboardPage';
import AimDashboardPage from './pages/AimDashboardPage';
import EventIntelligencePage from './pages/EventIntelligencePage';
import RouteIntelligencePage from './pages/RouteIntelligencePage';
import CompetitiveIntelligencePage from './pages/CompetitiveIntelligencePage';
import WeatherIntelligencePage from './pages/WeatherIntelligencePage';
import SearchConsolePage from './pages/SearchConsolePage';
import LocalIntelligencePage from './pages/LocalIntelligencePage';
import NowPage from './pages/hub/NowPage';
import ConversationsPage from './pages/hub/ConversationsPage';
import MailboxHealthPage from './pages/hub/MailboxHealthPage';
import TeamAdminPage from './pages/hub/TeamAdminPage';
import SendKillSwitchPage from './pages/hub/SendKillSwitchPage';
import HubClientsPage from './pages/hub/HubClientsPage';
import HubClientDetailPage from './pages/hub/HubClientDetailPage';
import HubProfilePage from './pages/hub/HubProfilePage';
import EffortPage from './pages/EffortPage';
import EffortHubPage from './pages/hub/EffortHubPage';

import WorkPage from './pages/hub/WorkPage';
import CapabilitiesPage from './pages/hub/CapabilitiesPage';
import SignalsAdminPage from './pages/hub/SignalsAdminPage';
// Portal — "MY WORK" client-facing page (Epic 3+). Served at /my-work, gated
// behind the `portal` feature flag exactly like the other dashboard.* pages
// (Epic 11 · S11.3). The page component lives under components/portal/.
import PortalPage from './components/portal/PortalPage';
import DashboardLayout from './components/DashboardShell';

// Component to handle auto-login on app load
const AuthChecker = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      // Skip auth check on public pages (login + password reset). The reset
      // page must NOT be redirected away from: the recovery token lives in
      // the URL fragment, and a navigate('/') here (stale/expired stored
      // token) would destroy the fragment before supabase-js can exchange it.
      if (location.pathname === '/' || location.pathname === '/reset-password') {
        setIsChecking(false);
        return;
      }

      const token = localStorage.getItem('token');

      // If no token, skip verification (ProtectedRoute will redirect)
      if (!token) {
        setIsChecking(false);
        return;
      }

      try {
        // Verify if the token is still valid
        const response = await verifyToken();
        const userData = response?.data || response;

        if (userData) {
          // Token is valid, user stays on current protected route
          setIsChecking(false);
        }
      } catch (error) {
        // Token is invalid or expired, clear storage and redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('username');
        localStorage.removeItem('role');
        localStorage.removeItem('client_id');
        localStorage.removeItem('user_id');
        localStorage.removeItem('rememberMe');
        localStorage.removeItem('avatar');
        // Note: 'theme_preference' (the old key) is intentionally NOT removed
        // here — ThemeContext owns the live key 'theme' and leaves it cached on
        // logout so the login page keeps the last theme. The stale
        // 'theme_preference' key, if present, is harmless (no reader remains
        // after Epic 3).
        navigate('/', { replace: true });
        setIsChecking(false);
      }
    };

    checkAuth();
  }, [navigate, location.pathname]);

  // 2026-09-02 — Safety net for recovery links that still point at the root.
  // resetPasswordForEmail now sends users to /reset-password (see
  // AuthContext.resetPassword), but links emailed before that change resolve
  // to the site URL (/). When such a link is opened, supabase-js exchanges the
  // recovery token and fires PASSWORD_RECOVERY — without this listener the user
  // lands on / logged in, with nowhere to set a password. Catch the event and
  // move them to the form. Guarded so a user already on /reset-password is not
  // re-navigated (ResetPasswordPage handles its own recovery state).
  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY' && location.pathname !== '/reset-password') {
        navigate('/reset-password', { replace: true });
      }
    });
    return () => data.subscription.unsubscribe();
  }, [navigate, location.pathname]);

  // Show loading spinner while checking auth
  if (isChecking) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white dark:bg-gray-950">
        <div className="spinner"></div>
      </div>
    );
  }

  return children;
};

const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  return token ? children : <Navigate to="/" replace />;
};

// 2026-09-14 — agency-internal staff are Client-Hub-only: they land on /hub/now
// on login and have NO option to reach the analytics dashboard. This guard
// bounces any staff role that hits /dashboard (direct URL, FeatureRoute redirect,
// or a stale bookmark) to /hub/now, so the "no dashboard" rule holds even outside
// the login flow. See src/lib/roles.js.
//
// 2026-10-06 — staff_owner is no longer a staff role here (isStaffRoleName is
// false for her): she is a toggle role that lands on /dashboard, so this guard
// passes her through unconditionally.
//
// 2026-10-07 — staff_manager is no longer a staff role here either (also
// promoted to a toggle role): same as staff_owner, the guard passes her through
// to /dashboard.
//
// 2026-09-28 (Epic 3, item 35 Q2) — the six full-access staff are the exception:
// canOpenClientDashboard is true for them, so they pass through to DashboardPage
// instead of bouncing back to /hub/now. Without this, Epic 2's "Dashboard"
// toggle would be a dead link. dashboardBlockedForRole (lib/roles.js) is NOT
// changed — it stays isStaffRoleName-only; the route guard reads the flag
// directly. The six still LAND on /hub/now on login (loginRouteForRole is
// unchanged); this guard only governs whether /dashboard itself loads.
// Exported for unit testing (App.StaffDashboardRedirect.test.js) — the guard's
// decision is the part worth pinning; mounting the whole App is too heavy.
export const StaffDashboardRedirect = ({ children }) => {
  const view = useView();
  if (view?.isStaff && !view?.canOpenClientDashboard) return <Navigate to="/hub/now" replace />;
  return children;
};

// Exported for unit testing (App.FeatureRoute.test.js).
export const FeatureRoute = ({ feature, children }) => {
  const { hasFeature, loading } = useFeatures();
  const view = useView();
  // Multi-Org Membership Epic 1 — role follows the active org, not a flattened
  // localStorage value. view.role is 99 until a profile/membership resolves.
  const role = view?.role ?? 99;
  if (loading) return null;
  // Super Admins and Managers bypass feature gates
  if (role === 0 || role === 2) return children;
  // Check user-level features first (fn_get_my_features resolves user > client > org > global)
  if (hasFeature(feature)) return children;
  // Fall back to per-client `feature_flags` scope='client' via ViewContext's
  // resolved map. ViewContext exposes `hasProjectFeature(shortKey)` which
  // reads scope='client' feature_flags for selectedProject.
  if (view?.hasProjectFeature?.(feature)) return children;
  // Not allowed YET is not the same as not allowed: on a fresh page load (a new tab, a refresh) the
  // selected client's features are still being read, and redirecting now would bounce a user who does
  // have the feature to the dashboard. Wait until they have settled, then decide.
  if (view?.projectFlagsReady === false) return null;
  const redirectPath = role === 3 ? "/client-dashboard" : "/dashboard";
  return <Navigate to={redirectPath} replace />;
};

const NotFoundPage = () => (
  <div className="flex flex-col min-h-screen bg-[#f8fbfe] dark:bg-gray-950 transition-colors duration-200">
    <div className="flex-grow flex flex-col items-center justify-center">
      <h1 className="text-5xl font-extrabold text-[#32B1FF] mb-2">404</h1>
      <h2 className="text-2xl font-semibold text-gray-700 dark:text-gray-200 mb-3">Page Not Found</h2>
      <p className="text-gray-500 dark:text-gray-400 mb-8 text-center max-w-xl">
        Sorry, the page you're looking for doesn't exist or has been moved.<br />
        If you think this is a mistake, please contact our support team.<br />
        Otherwise, you can return to the login screen below.
      </p>
      <a
        href="/"
        className="px-6 py-3 bg-[#32B1FF] hover:bg-[#1a9ce6] text-white rounded-lg font-semibold text-base transition"
      >
        Return to Login
      </a>
    </div>
    <footer className="w-full py-4 text-center text-xs text-gray-300 dark:text-gray-600 bg-transparent absolute bottom-0">
      © {new Date().getFullYear()} Insider Perks. All rights reserved.
    </footer>
  </div>
);


import { Navigate, useParams } from "react-router-dom";

// Redirect helper for the legacy /project/:projectId route → /client/:projectId.
// Extract-then-Navigate lets us preserve the param without changing the URL
// scheme the ProjectDetailPage consumes.
const LegacyProjectRedirect = () => {
  const { projectId } = useParams();
  return <Navigate to={`/client/${projectId}`} replace />;
};

function App() {
  return (
    <ThemeProvider>
      <NotificationProvider>
        <NotificationModal />
        <ToastProvider>
          <ToastContainer />
        <Router>
          <AuthProvider>
          <FeaturesProvider>
          <ViewProvider>
          <WorkspaceProvider>
          <TimerProvider>
          <AuthChecker>
          <Routes>
            <Route path="/" element={<LoginPage />} />
            {/* Public: Supabase recovery links land here (redirectTo is
                `${origin}/reset-password` — see AuthContext.resetPassword). */}
            <Route path="/reset-password" element={<ResetPasswordPage />} />

          <Route path="/subscribe" element={<SubscribeUpsellPage />} />

          {/* ── Persistent dashboard shell ─────────────────────────────────────
              The header (AdminHeader/ClientHeader) + sidebar (NavSidebar) mount
              ONCE here. Each page in this group only renders its <main> content
              and declares its active nav keys via usePageMeta(). Because the
              chrome no longer lives inside each route element, navigating between
              /dashboard, /my-work, /bookings, /visitors, … no longer tears down
              and re-mounts the whole shell — transitions are seamless. Pages that
              still render their own chrome stay OUTSIDE this group until migrated. */}
          <Route element={<DashboardLayout />}>
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <StaffDashboardRedirect>
                  <DashboardPage />
                </StaffDashboardRedirect>
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-work"
            element={
              <ProtectedRoute>
                <FeatureRoute feature="portal">
                  <PortalPage />
                </FeatureRoute>
              </ProtectedRoute>
            }
          />
          <Route path="/google-ads" element={<ProtectedRoute><FeatureRoute feature="google_ads"><GoogleAdsPage /></FeatureRoute></ProtectedRoute>} />
          <Route path="/google-analytics" element={<ProtectedRoute><FeatureRoute feature="google_analytics"><GoogleAnalyticsPage /></FeatureRoute></ProtectedRoute>} />
          <Route path="/route-intelligence" element={<ProtectedRoute><FeatureRoute feature="route_intelligence"><RouteIntelligencePage /></FeatureRoute></ProtectedRoute>} />
          <Route path="/effort" element={<ProtectedRoute><EffortPage /></ProtectedRoute>} />
          <Route path="/event-intelligence" element={<ProtectedRoute><FeatureRoute feature="event_intelligence"><EventIntelligencePage /></FeatureRoute></ProtectedRoute>} />
          <Route path="/ai-visibility" element={<ProtectedRoute><FeatureRoute feature="aim"><AimDashboardPage /></FeatureRoute></ProtectedRoute>} />
          <Route path="/competitive-intelligence" element={<ProtectedRoute><FeatureRoute feature="competitive_intelligence"><CompetitiveIntelligencePage /></FeatureRoute></ProtectedRoute>} />
          <Route path="/search-console" element={<ProtectedRoute><FeatureRoute feature="search_console"><SearchConsolePage /></FeatureRoute></ProtectedRoute>} />
          <Route path="/bookings" element={<ProtectedRoute><FeatureRoute feature="bookings"><BookingsPage /></FeatureRoute></ProtectedRoute>} />
          <Route path="/visitors" element={<ProtectedRoute><FeatureRoute feature="cdp"><CdpDashboardPage /></FeatureRoute></ProtectedRoute>} />
          <Route path="/weather-intelligence" element={<ProtectedRoute><FeatureRoute feature="weather_intelligence"><WeatherIntelligencePage /></FeatureRoute></ProtectedRoute>} />
          <Route path="/local-intelligence" element={<ProtectedRoute><FeatureRoute feature="local_intelligence"><LocalIntelligencePage /></FeatureRoute></ProtectedRoute>} />
          </Route>
          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <UserManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/organizations"
            element={
              <ProtectedRoute>
                <CompanyManagementPage />
              </ProtectedRoute>
            }
          />
          {/* Legacy alias — remove after ~1 release once bookmarks migrate. */}
          <Route path="/companies" element={<Navigate to="/organizations" replace />} />
          <Route
            path="/clients"
            element={
              <ProtectedRoute>
                <ProjectManagementPage />
              </ProtectedRoute>
            }
          />
          <Route path="/projects" element={<Navigate to="/clients" replace />} />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <UpsellNotificationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/:projectId"
            element={
              <ProtectedRoute>
                <ProjectDetailPage />
              </ProtectedRoute>
            }
          />
          {/* Legacy alias for the singular admin-project route. */}
          <Route path="/project/:projectId" element={<LegacyProjectRedirect />} />
          <Route
            path="/transcripts/:transcriptId"
            element={
              <ProtectedRoute>
                <TranscriptDetailPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/client-dashboard"
            element={
              <ProtectedRoute>
                <ClientDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client-projects/:projectId"
            element={
              <ProtectedRoute>
                <ClientProjectDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client-transcripts/:transcriptId"
            element={
              <ProtectedRoute>
                <ClientTranscriptDetailPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/price-comparison"
            element={
              <ProtectedRoute>
                <PriceComparisonPage />
              </ProtectedRoute>
            }
          />

          {/* /bookings moved into the <DashboardLayout/> group above */}


          {/* /google-ads moved into the <DashboardLayout/> group above */}

          {/* /google-analytics moved into the <DashboardLayout/> group above */}

          {/* /visitors moved into the <DashboardLayout/> group above */}

          {/* /event-intelligence moved into the <DashboardLayout/> group above */}

          {/* /ai-visibility moved into the <DashboardLayout/> group above (the
              /ai-monitor + /aim aliases below still redirect to it). */}
          {/* Legacy paths — bookmarks + copy-pasted links land here.
              Renamed to /ai-visibility on 2026-07-29. */}
          <Route path="/ai-monitor" element={<Navigate to="/ai-visibility" replace />} />
          <Route path="/aim" element={<Navigate to="/ai-visibility" replace />} />

          {/* /route-intelligence moved into the <DashboardLayout/> group above */}

          {/* /competitive-intelligence moved into the <DashboardLayout/> group above */}

          {/* /weather-intelligence moved into the <DashboardLayout/> group above */}

          {/* /local-intelligence moved into the <DashboardLayout/> group above */}

          {/* /search-console moved into the <DashboardLayout/> group above (the
              /search + /search-intelligence aliases below still redirect to it). */}

          {/* The page is served at `/search-console`, matching the sidebar label and the
              `search_console` backend contract.
              Two aliases redirect into it rather than 404:
                /search               — the spec's §12 checklist calls the route this.
                /search-intelligence  — what the page shipped under while the feature carried
                                        the Search Intelligence name; keeps shared links alive.
              Same pattern as /ai-monitor and /aim → /ai-visibility above. */}
          <Route path="/search" element={<Navigate to="/search-console" replace />} />
          <Route path="/search-intelligence" element={<Navigate to="/search-console" replace />} />

          {/* Portal — "MY WORK" route moved into the persistent <DashboardLayout/>
              group above so the shell doesn't remount when navigating to/from it. */}

          {/* Hub — default landing. /hub → /hub/now: Now is the hub's home page
              (the ranked feed an account manager opens into, per the one-line
              compass in CLIENTHUB_KNOWLEDGE_BASE.md §1). All /hub/* children are
              ProtectedRoute-only — the bare /hub has no page of its own, so this
              redirect is the entry point. */}
          <Route path="/hub" element={<Navigate to="/hub/now" replace />} />

          {/* Hub — internal-team ranked feed. ProtectedRoute only (no FeatureRoute):
              visibility is governed server-side — a non-ops caller's fn_now returns
              nothing / a gated payload → <NotPermitted>. */}
          <Route
            path="/hub/now"
            element={
              <ProtectedRoute>
                <NowPage />
              </ProtectedRoute>
            }
          />
          {/* ClientHub — internal account-manager surface. Page-level role gate
              lives inside each page (canAccessHub), not a FeatureRoute: it is
              internal-only (roles 0/1/2), never clients (role 3), and needs no
              per-project entitlement. */}
          <Route
            path="/hub/clients"
            element={
              <ProtectedRoute>
                <HubClientsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/hub/clients/:id"
            element={
              <ProtectedRoute>
                <HubClientDetailPage />
              </ProtectedRoute>
            }
          />

          {/* Internal-ops "hub" — staff time/effort review. Not a per-client
              feature: it gates on ProtectedRoute alone (login), NOT a FeatureRoute,
              because the client feature-flag model doesn't apply to a staff tool.
              Whether /hub/* should sit behind a dedicated staff permission is an
              open §7 question — see the TODO(hub-gating) in EffortPage.

              The identical Effort surface renders in two shells (both wrap the
              shared <EffortContent>): inside the ClientHub HubShell here, so the
              hub sidebar's "Effort" keeps the page in the hub's own column; and in
              the legacy dashboard chrome at `/effort` below, reached from the
              customer NavSidebar. */}
          <Route
            path="/hub/effort"
            element={
              <ProtectedRoute>
                <EffortHubPage />
              </ProtectedRoute>
            }
          />
          {/* /effort moved into the <DashboardLayout/> group above */}

          {/* Hub — Conversations (Charles, Epic C1). Three objects, not one
              list: channels & DMs are places; threads are a queue. The DB
              enforces it, so the page never offers resolve/assign on a channel
              or DM. Reads go through fn_* that read v_my_messages (the DM/company
              walls apply in SQL). Writes go through fn_compose_send via
              <Composer> (Epic C3). RPCs are UNCONFIRMED until the §0 probe
              (docs/CLIENTHUB-CONVERSATIONS-EPICS.md) — the page degrades through
              <ErrorState> until they deploy. */}
          <Route
            path="/hub/conversations"
            element={
              <ProtectedRoute>
                <ConversationsPage />
              </ProtectedRoute>
            }
          />

          {/* Hub — Work (`/hub/work`). One owner, one obligation-driven page.
            Buckets computed by fn_work_bucket in SQL; nine pipelines, each with
            its own status vocabulary — no global statuses, no board, no folders.
            Reads: fn_work_list (paginated, composite cache key) + fn_work_detail.
            Writes: fn_work_create/move/assign/update/bulk + fn_compose_send.
            Slide-out detail IS the page — reply, assign, close from here.
            Charles owns: <ClientSlideOut>, <Composer>, <SituationCard>,
            <Timeline>, <ErrorBoundary>, <EmptyState>, <DrillSheet>.
            Known bugs: fn_work_bucket misclassifies null owners as waiting_on_me;
            fn_work_list parameter interaction bug (p_bucket + filter = 0);
            fn_suggest_communicator not yet in DB.
            Unowned bucket is 0; should be 87% per spec — blocked on bucket fix. */}
          <Route
            path="/hub/work"
            element={
              <ProtectedRoute>
                <WorkPage />
              </ProtectedRoute>
            }
          />

          {/* ── Admin routes (Epic 3 · S3.3) ─────────────────────────────────────
              Organizations, Clients, Users move OUT of the top nav into
              /hub/admin/*. The old routes stay as aliases so existing links
              don't 404. The hub sidebar's Admin section points here. */}
          <Route path="/hub/admin/organizations" element={<Navigate to="/organizations" replace />} />
          <Route path="/hub/admin/clients" element={<Navigate to="/clients" replace />} />
          <Route path="/hub/admin/users" element={<Navigate to="/users" replace />} />

          {/* Hub — Profile. The hub top-bar avatar links here so opening the
              profile from inside the hub keeps the hub's sidebar + top bar in
              view, instead of dropping to the dashboard chrome at /profile.
              Same ProfilePage content, just wrapped in HubShell. */}

          <Route
            path="/hub/profile"
            element={
              <ProtectedRoute>
                <HubProfilePage />
              </ProtectedRoute>
            }
          />

          {/* Hub — Mailbox health (Charles, Epic C5). An admin surface, not a
              page-affecting feature. The send pipeline's on/off + sync health +
              backfill progress per connected mailbox. Gated by agent.manage on
              the page (the closest existing admin capability; a backend
              mailbox.manage is the eventual right gate). RPCs UNCONFIRMED until
              a probe pass — the page degrades through <ErrorState>/<NotPermitted>. */}
          <Route
            path="/hub/admin/mailboxes"
            element={
              <ProtectedRoute>
                <MailboxHealthPage />
              </ProtectedRoute>
            }
          />

          {/* Hub — Send kill switch (Epic D2). The admin surface for the
              send-pipeline kill switch: pause (required reason) / resume every
              client's outbound delivery. Reads v_send_health; writes through
              fn_send_pause(p_reason) / fn_send_resume(). Gated by agent.manage on
              the page. Never fakes a sent state — the page states the real
              `enabled` value (knowledge-base §18). */}
          <Route
            path="/hub/admin/send"
            element={
              <ProtectedRoute>
                <SendKillSwitchPage />
              </ProtectedRoute>
            }
          />

          {/* Hub — Admin → Capabilities (Team & Admin §2). "What the company can
              do, and where it is fragile." Tier 5 — raw tables; one loud moment:
              the single-points-of-failure list. Read-only, frontend-direct: the
              page reads v_capability_coverage / mv_member_capability /
              ops_capabilities through src/api/hub/capabilities.js and calls
              fn_who_can for the "who can" lookup. There is NO BFF piece for this
              screen — every read resolves auth.uid() itself. Gated by
              agent.manage on the page (the closest existing admin capability). */}
          <Route
            path="/hub/admin/capabilities"
            element={
              <ProtectedRoute>
                <CapabilitiesPage />
              </ProtectedRoute>
            }
          />

          {/* Hub — Admin → Team reporting-line editor (Team & Admin §1, Epic 3).
              An admin (Brian, Aly — admin.manage) can set/remove a manager and set
              someone to top-of-tree. Non-holders see the tree read-only (controls
              hidden, not disabled). The DB refuses cycles + self-management
              server-side; the UI surfaces the refusal message verbatim. */}

          <Route
            path="/hub/admin/team"
            element={
              <ProtectedRoute>
                <TeamAdminPage />
              </ProtectedRoute>
            }
          />

          {/* Hub — Admin → Signals (Team & Admin §4, Epic 4). "Now is driven
              entirely by rows." Toggle is_active (on/off) and edit rank
              (ordering) on ops_signals. No Add/Rename/Delete — a signal is
              defined by its detect_sql, which is engineering's to write. Read +
              write, frontend-direct; gated by admin.manage (the wall is the
              database — the UI gate is a convenience). Non-holders see the list
              read-only (controls disabled). The "nothing to change" sentence
              from fn_signal_set is surfaced verbatim. */}
          <Route
            path="/hub/admin/signals"
            element={
              <ProtectedRoute>
                <SignalsAdminPage />
              </ProtectedRoute>
            }
          />

          {/* ── Workspace route prefixes (Epic 2 · S2.3) ──────────────────────
              The prefix IS the workspace (00_CONTRACT.md §10): /p = park,
              /o = operator, /hub = team, /r = research. The same page slug
              renders under the right lens — the database decides who sees
              what (every function checks auth.uid() itself). These aliases
              let a page appear in a second workspace without duplicating the
              route definition. As park/operator/research pages are built they
              register here under their prefix; for now the hub pages are the
              canonical set and the other prefixes redirect to the hub
              equivalent so no link 404s. */}
          <Route path="/p/:clientId/now" element={<Navigate to="/hub/now" replace />} />
          <Route path="/p/:clientId/clients" element={<Navigate to="/hub/clients" replace />} />
          <Route path="/p/:clientId/clients/:id" element={<Navigate to="/hub/clients/:id" replace />} />
          <Route path="/o/:orgId/now" element={<Navigate to="/hub/now" replace />} />
          <Route path="/o/:orgId/clients" element={<Navigate to="/hub/clients" replace />} />
          <Route path="/r/now" element={<Navigate to="/hub/now" replace />} />
          <Route path="/r/clients" element={<Navigate to="/hub/clients" replace />} />
          <Route path="/p/:clientId/conversations" element={<Navigate to="/hub/conversations" replace />} />
          <Route path="/o/:orgId/conversations" element={<Navigate to="/hub/conversations" replace />} />
          <Route path="/r/conversations" element={<Navigate to="/hub/conversations" replace />} />
          <Route path="/p/:clientId/admin/mailboxes" element={<Navigate to="/hub/admin/mailboxes" replace />} />
          <Route path="/o/:orgId/admin/mailboxes" element={<Navigate to="/hub/admin/mailboxes" replace />} />
          <Route path="/r/admin/mailboxes" element={<Navigate to="/hub/admin/mailboxes" replace />} />
          <Route path="/p/:clientId/admin/send" element={<Navigate to="/hub/admin/send" replace />} />
          <Route path="/o/:orgId/admin/send" element={<Navigate to="/hub/admin/send" replace />} />
          <Route path="/r/admin/send" element={<Navigate to="/hub/admin/send" replace />} />
          <Route path="/p/:clientId/admin/capabilities" element={<Navigate to="/hub/admin/capabilities" replace />} />
          <Route path="/o/:orgId/admin/capabilities" element={<Navigate to="/hub/admin/capabilities" replace />} />
          <Route path="/r/admin/capabilities" element={<Navigate to="/hub/admin/capabilities" replace />} />
          <Route path="/p/:clientId/admin/signals" element={<Navigate to="/hub/admin/signals" replace />} />
          <Route path="/o/:orgId/admin/signals" element={<Navigate to="/hub/admin/signals" replace />} />
          <Route path="/r/admin/signals" element={<Navigate to="/hub/admin/signals" replace />} />

          <Route path="*" element={<NotFoundPage />} />

          <Route path="/count-summary" element={<CountSummaryPage />} />

        </Routes>
        </AuthChecker>
          </TimerProvider>
          </WorkspaceProvider>
          </ViewProvider>
          </FeaturesProvider>
          </AuthProvider>
        </Router>
        </ToastProvider>
      </NotificationProvider>
    </ThemeProvider>
  );
}

export default App;
